import type { DropdownState } from './types';
import type { BehaviorConfig } from '~/components/SearchSuggest/types';
import type { Ref } from 'vue';

interface UseSuggestDropdownOptions {
	behaviorConfig: BehaviorConfig;
	commandEl: Readonly<Ref<HTMLElement | null>>;
	searchTerm: Ref<string>;
	hasSelectedValue: Ref<boolean>;
	onOpen?: () => void;
	onClose?: () => void;
	onCloseModal?: () => void;
}

/**
 * Manages dropdown open/close state, backup/restore, and keyboard navigation
 * Centralizes placeholder logic and escape/click-outside behavior
 *
 * @param options - Configuration and state refs
 * @returns Dropdown control functions and state
 */
export function useSuggestDropdown(options: UseSuggestDropdownOptions) {
	const {
		behaviorConfig,
		commandEl,
		searchTerm,
		hasSelectedValue,
		onOpen,
		onClose,
		onCloseModal
	} = options;

	// Dropdown state
	const isOpen = ref<boolean>(false);
	const backupSearchTerm = ref<string>('');
	const isUserFocusedOpening = ref<boolean>(false);
	const isNavigatingWithKeyboard = ref<boolean>(false);

	// Placeholders
	const placeholderClosed = ref<string>('');
	const placeholderOpen = ref<string>('');

	/**
	 * Open the dropdown
	 */
	function open() {
		isOpen.value = true;
		onOpen?.();
	}

	/**
	 * Close the dropdown
	 */
	function close() {
		isOpen.value = false;

		if (behaviorConfig.closeOnSelect) {
			onCloseModal?.();
		}

		onClose?.();
	}

	/**
	 * Set user focused opening state
	 */
	function setUserFocusedOpening(value: boolean) {
		isUserFocusedOpening.value = value;
	}

	/**
	 * Mark keyboard navigation as active
	 */
	function setNavigatingWithKeyboard(value: boolean) {
		isNavigatingWithKeyboard.value = value;
	}

	/**
	 * Handle dropdown opening - backup search term if needed
	 */
	function handleOpening() {
		const currentTerm = searchTerm.value;

		// If no term or already cleared, skip
		if (!currentTerm || hasSelectedValue.value === false) {
			isUserFocusedOpening.value = false;
			return;
		}

		// Backup and clear if user focused and has valid term
		if (isUserFocusedOpening.value && currentTerm) {
			backupSearchTerm.value = currentTerm;
			searchTerm.value = '';
		}

		isUserFocusedOpening.value = false;
	}

	/**
	 * Handle dropdown closing - restore backup if needed
	 */
	function handleClosing() {
		const shouldRestoreBackup =
			backupSearchTerm.value && !hasSelectedValue.value && !searchTerm.value;

		if (shouldRestoreBackup) {
			searchTerm.value = backupSearchTerm.value;
		}

		backupSearchTerm.value = '';
	}

	/**
	 * Handle mousedown for click-outside detection
	 */
	function handleMouseDown(event: MouseEvent) {
		if (!behaviorConfig.closeOnClickOutside) return;

		const element = commandEl.value;
		if (element && !element.contains(event.target as Node)) {
			close();
		}
	}

	/**
	 * Handle escape key
	 */
	function handleEscape() {
		if (behaviorConfig.closeOnClickOutside) {
			close();
		}
	}

	/**
	 * Clear backup state
	 */
	function clearBackup() {
		backupSearchTerm.value = '';
	}

	/**
	 * Set placeholder text
	 */
	function setPlaceholders(closed: string, opened?: string) {
		placeholderClosed.value = closed;
		placeholderOpen.value = opened || closed;
	}

	// Watch dropdown state changes
	watch(isOpen, (newValue, oldValue) => {
		const isOpening = newValue && !oldValue;
		const isClosing = !newValue && oldValue;

		if (isOpening) {
			handleOpening();
		} else if (isClosing) {
			handleClosing();
		}
	});

	// Event listeners
	useEventListener(document, 'mousedown', handleMouseDown);
	onKeyStroke('Escape', e => {
		e.preventDefault();
		handleEscape();
	});

	return {
		// State
		isOpen,
		backupSearchTerm: readonly(backupSearchTerm),
		isUserFocusedOpening: readonly(isUserFocusedOpening),
		isNavigatingWithKeyboard: readonly(isNavigatingWithKeyboard),
		placeholderClosed: readonly(placeholderClosed),
		placeholderOpen: readonly(placeholderOpen),

		// Actions
		open,
		close,
		setUserFocusedOpening,
		setNavigatingWithKeyboard,
		clearBackup,
		setPlaceholders
	};
}
